import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Spinner } from '@/components/Spinner';
import { Page } from '@/components/layout/Page';

/* ── Types ────────────────────────────────────────────────────────────── */
type Dataset = {
  id: string;
  name: string;
  tables: string;
  rows: string;
  status: string;
  statusLabel: string;
  freshness: string;
  freshnessColor: string;
};
type Group = { module: string; datasets: Dataset[] };
type ScoreRow = { l: string; v: string; color: string };
type Anomaly = { name: string; detail: string };
type Scorecard = {
  title: string;
  value: string;
  pct: number;
  color: string;
  desc: string;
  rows?: ScoreRow[];
  anomalies?: Anomaly[];
};
type NullRow = {
  field: string;
  nullRate: string;
  rateColor: string;
  usedBy: string;
  status: string;
  statusColor: string;
};
type AttributedTable = { layer: string; table: string; database: string; label: string };
type Detail = {
  module: string;
  name: string;
  meta: string;
  overall: { score: string; pct: number; color: string; note: string };
  scorecards: Scorecard[];
  nullRates: { title: string; sub: string; rows: NullRow[] };
  attributedTable?: AttributedTable | null;
  sample?: Record<string, unknown>[];
};
type QualityData = {
  indicator: string;
  listHeader: { title: string; sub: string };
  defaultDatasetId: string;
  groups: Group[];
};

/* ── Colors ───────────────────────────────────────────────────────────── */
const C = {
  t1: '#1A1C2E',
  t2: '#5C5F72',
  t3: '#9295A5',
  border: '#E4E0D8',
  blue: '#274483',
  warm: '#F5F6F8',
  pos: '#2A8055',
  neg: '#BC2E3D',
  warn: '#D27A24',
};
const tone = (c: string) =>
  c === 'positive' ? C.pos : c === 'warning' ? C.warn : c === 'negative' ? C.neg : c === 'muted' ? C.t3 : C.t1;
const statusBadge = (status: string) =>
  status === 'current' ? { bg: 'rgba(42,128,85,.1)', color: C.pos } : { bg: 'rgba(210,122,36,.1)', color: C.warn };

const cardStyle: React.CSSProperties = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8 };
const scLabel: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: C.t3,
  textTransform: 'uppercase',
  letterSpacing: '.07em',
};

const cellText = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return Array.isArray(v) ? `[ ${v.length} ]` : '{ … }';
  return String(v);
};

/* ── Dataset list (left) ──────────────────────────────────────────────── */
function DatasetRow({ d, active, onClick }: { d: Dataset; active: boolean; onClick: () => void }) {
  const badge = statusBadge(d.status);
  return (
    <div
      onClick={onClick}
      style={{
        padding: '10px 14px',
        cursor: 'pointer',
        borderBottom: `1px solid ${C.border}`,
        background: active ? 'rgba(39,68,131,.07)' : undefined,
        borderLeft: active ? `3px solid ${C.blue}` : '3px solid transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: C.t1 }}>{d.name}</div>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 100,
            background: badge.bg,
            color: badge.color,
          }}
        >
          {d.statusLabel}
        </span>
      </div>
      <div style={{ fontSize: 10, color: C.t3, fontFamily: 'monospace' }}>{d.tables}</div>
      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        <span style={{ fontSize: 10, color: C.t3 }}>{d.rows}</span>
        <span style={{ fontSize: 10, fontWeight: 600, color: tone(d.freshnessColor) }}>{d.freshness}</span>
      </div>
    </div>
  );
}

/* ── Scorecard ────────────────────────────────────────────────────────── */
function ScorecardCard({ s }: { s: Scorecard }) {
  return (
    <div style={{ ...cardStyle, padding: '14px 16px' }}>
      <div style={{ ...scLabel, marginBottom: 8 }}>{s.title}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone(s.color), marginBottom: 4 }}>{s.value}</div>
      <div style={{ height: 4, borderRadius: 2, background: C.border, marginBottom: 6 }}>
        <div style={{ height: 4, borderRadius: 2, background: tone(s.color), width: `${s.pct}%` }} />
      </div>
      <div style={{ fontSize: 10, color: C.t3 }}>{s.desc}</div>
      {s.rows && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {s.rows.map((r) => (
            <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
              <span style={{ color: C.t2 }}>{r.l}</span>
              <span style={{ color: tone(r.color) }}>{r.v}</span>
            </div>
          ))}
        </div>
      )}
      {s.anomalies && s.anomalies.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {s.anomalies.map((a) => (
            <div
              key={a.name}
              style={{
                padding: '4px 8px',
                background: 'rgba(210,122,36,.06)',
                borderRadius: 3,
                borderLeft: `2px solid ${C.warn}`,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{a.name}</div>
              <div style={{ fontSize: 9, color: C.t3 }}>{a.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────── */
export function DataQuality() {
  const { data, isLoading } = useQuery<QualityData>({
    queryKey: ['data-app', 'data-quality'],
    queryFn: () => fetch('/api/data-app/data-quality').then((r) => r.json()),
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const groups = useMemo(() => data?.groups ?? [], [data]);
  const activeId = selectedId ?? data?.defaultDatasetId ?? null;

  // Per-dataset detail (lazy, on selection) — real sampled null-rates + freshness + sample rows.
  const { data: detail, isFetching: detailLoading } = useQuery<Detail>({
    queryKey: ['data-app', 'data-quality', activeId],
    queryFn: () => fetch(`/api/data-app/data-quality/${activeId}`).then((r) => r.json()),
    enabled: !!activeId,
    staleTime: 5 * 60 * 1000,
  });

  const th: React.CSSProperties = {
    padding: '7px 14px',
    textAlign: 'left',
    fontSize: 8.5,
    fontWeight: 700,
    color: C.t3,
    textTransform: 'uppercase',
    letterSpacing: '.07em',
    borderBottom: `1px solid ${C.border}`,
  };
  const td: React.CSSProperties = { padding: '8px 14px', borderBottom: `1px solid ${C.border}` };

  return (
    <Page
      title="Data Quality"
      subheader="SAP source datasets ingested by Keystone — completeness, freshness, and anomaly status for each module."
      fillHeight
      actions={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            alignSelf: 'flex-start',
            marginTop: 6,
          }}
        >
          <div
            className="animate-pulse"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: C.pos,
              boxShadow: '0 0 0 3px rgba(42,128,85,.2)',
            }}
          />
          <span style={{ fontSize: 11, fontWeight: 700, color: C.pos }}>
            {data?.indicator ?? 'Live — processing now'}
          </span>
        </div>
      }
    >
      <div
        className="flex flex-1 min-h-0 overflow-hidden h-full bg-white"
        style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}
      >
        {isLoading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner size={28} />
          </div>
        ) : (
          <>
            {/* LEFT: dataset list */}
            <div
              style={{
                width: 340,
                flexShrink: 0,
                borderRight: `1px solid ${C.border}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                background: C.warm,
              }}
            >
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{data?.listHeader.title}</div>
                <div style={{ fontSize: 10, color: C.t3, marginTop: 1 }}>{data?.listHeader.sub}</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {groups.map((g) => (
                  <div key={g.module}>
                    <div
                      style={{
                        padding: '8px 12px',
                        background: C.warm,
                        borderBottom: `1px solid ${C.border}`,
                        borderTop: `1px solid ${C.border}`,
                      }}
                    >
                      <div style={{ ...scLabel, letterSpacing: '.08em' }}>{g.module}</div>
                    </div>
                    {g.datasets.map((d) => (
                      <DatasetRow key={d.id} d={d} active={activeId === d.id} onClick={() => setSelectedId(d.id)} />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: per-dataset detail (updates on selection) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: C.warm }}>
              {!detail ? (
                detailLoading ? (
                  <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
                    <Spinner size={26} />
                  </div>
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: C.t3 }}>Select a dataset</div>
                )
              ) : (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ ...scLabel, letterSpacing: '.08em', marginBottom: 4 }}>{detail.module}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, letterSpacing: '-0.02em' }}>
                      {detail.name}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.t3, marginTop: 2 }}>{detail.meta}</div>
                    {/* Link to the attributed nile table on the Transformations page (permalink ?id=<layer>/<table>) */}
                    {detail.attributedTable && (
                      <div style={{ fontSize: 10.5, marginTop: 6, color: C.t2 }}>
                        Mapped to{' '}
                        <span style={{ fontFamily: 'monospace', color: C.t1 }}>{detail.attributedTable.label}</span> ·{' '}
                        <Link
                          to={`/data/transformations?id=${encodeURIComponent(
                            `${detail.attributedTable.layer}/${detail.attributedTable.table}`,
                          )}`}
                          style={{ color: C.blue, fontWeight: 600, textDecoration: 'none' }}
                        >
                          View in Transformations →
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Overall score */}
                  <div
                    style={{
                      ...cardStyle,
                      padding: '16px 20px',
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 20,
                    }}
                  >
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div
                        style={{
                          fontSize: 36,
                          fontWeight: 700,
                          color: tone(detail.overall.color),
                          letterSpacing: '-0.03em',
                        }}
                      >
                        {detail.overall.score}
                      </div>
                      <div style={{ ...scLabel }}>Overall Quality</div>
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ height: 8, borderRadius: 4, background: C.border }}>
                        <div
                          style={{
                            height: 8,
                            borderRadius: 4,
                            background: tone(detail.overall.color),
                            width: `${detail.overall.pct}%`,
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 10.5, color: C.t2 }}>{detail.overall.note}</div>
                    </div>
                  </div>

                  {/* Scorecard grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    {detail.scorecards.map((s) => (
                      <ScorecardCard key={s.title} s={s} />
                    ))}
                  </div>

                  {/* Null rate table */}
                  <div style={{ ...cardStyle, overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{detail.nullRates.title}</div>
                      <div style={{ fontSize: 10, color: C.t3, marginTop: 1 }}>{detail.nullRates.sub}</div>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: C.warm }}>
                          <th style={th}>Field</th>
                          <th style={th}>Null Rate</th>
                          <th style={th}>Used By</th>
                          <th style={th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.nullRates.rows.map((r, i) => (
                          <tr key={r.field} style={{ background: i % 2 === 1 ? 'rgba(0,0,0,.01)' : undefined }}>
                            <td style={{ ...td, fontWeight: 600, fontFamily: 'monospace', fontSize: 10 }}>{r.field}</td>
                            <td style={{ ...td, color: tone(r.rateColor), fontWeight: 700 }}>{r.nullRate}</td>
                            <td style={{ ...td, fontSize: 10, color: C.t3 }}>{r.usedBy}</td>
                            <td style={td}>
                              <span style={{ fontSize: 9, fontWeight: 700, color: tone(r.statusColor) }}>
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Sample rows (real, from IO /read preview) */}
                  {detail.sample && detail.sample.length > 0 && (
                    <div style={{ ...cardStyle, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Sample Rows</div>
                        <div style={{ fontSize: 10, color: C.t3, marginTop: 1 }}>
                          Live preview from {detail.attributedTable?.label}
                        </div>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 10 }}>
                          <thead>
                            <tr style={{ background: C.warm }}>
                              {Object.keys(detail.sample[0]).map((k) => (
                                <th key={k} style={{ ...th, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                  {k}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {detail.sample.map((row, i) => (
                              <tr key={i}>
                                {Object.keys(detail.sample![0]).map((k) => (
                                  <td
                                    key={k}
                                    style={{
                                      ...td,
                                      fontFamily: 'monospace',
                                      color: C.t2,
                                      whiteSpace: 'nowrap',
                                      maxWidth: 220,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                    title={typeof row[k] === 'object' ? JSON.stringify(row[k]) : undefined}
                                  >
                                    {cellText(row[k])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
